import { logger } from '../../utils/logger';

const USE_CACHE = (import.meta.env.VITE_USE_LOCAL_CACHE ?? 'true') === 'true';
const TTL_MS = 5 * 60 * 1000; // 5 minutes for "fresh" data
const LONG_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours for "visited before" cache
const ns = (weekKey: string, section: string) => `dash:${weekKey}:${section}`;
const VISITED_KEY = 'dash:visited:updates';

export const Cache = {
  get<T>(weekKey: string, section: string): { value: T|undefined, fresh: boolean } {
    if (!USE_CACHE) return { value: undefined, fresh: false };
    try {
      const raw = localStorage.getItem(ns(weekKey, section));
      if (!raw) return { value: undefined, fresh: false };
      const parsed = JSON.parse(raw) as { v: T; t: number };
      const age = Date.now() - parsed.t;
      // Consider data "fresh" if within 5 min, but still return cached data up to 24 hours
      const fresh = age < TTL_MS;
      // Return cached data even if not "fresh" (for faster subsequent loads)
      if (age < LONG_TTL_MS) {
      return { value: parsed.v, fresh };
      }
      // Data too old, clear it
      localStorage.removeItem(ns(weekKey, section));
      return { value: undefined, fresh: false };
    } catch { return { value: undefined, fresh: false }; }
  },
  set<T>(weekKey: string, section: string, v: T) {
    if (!USE_CACHE) return;
    
    try {
      localStorage.setItem(ns(weekKey, section), JSON.stringify({ v, t: Date.now() }));
      // Mark that user has visited Updates section
      try {
        localStorage.setItem(VISITED_KEY, 'true');
      } catch (e) {
        // Ignore VISITED_KEY errors (non-critical)
      }
    } catch (error) {
      // Handle QuotaExceededError with LRU eviction
      if (error instanceof DOMException && (error.code === 22 || error.name === 'QuotaExceededError')) {
        logger.warn('localStorage quota exceeded, evicting oldest cache entries', { weekKey, section }, 'LocalCache');
        
        // LRU eviction: Remove oldest cache entries (by timestamp)
        const cacheEntries: Array<{ key: string; timestamp: number }> = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('dash:')) {
            try {
              const raw = localStorage.getItem(key);
              if (raw) {
                const parsed = JSON.parse(raw) as { v: unknown; t: number };
                cacheEntries.push({ key, timestamp: parsed.t });
              }
            } catch {
              // Skip invalid entries
            }
          }
        }
        
        // Sort by timestamp (oldest first) and remove 20% of entries
        cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
        const toRemove = Math.max(1, Math.floor(cacheEntries.length * 0.2));
        for (let i = 0; i < toRemove; i++) {
          localStorage.removeItem(cacheEntries[i].key);
        }
        
        // Retry the set operation
        try {
          localStorage.setItem(ns(weekKey, section), JSON.stringify({ v, t: Date.now() }));
        } catch (retryError) {
          logger.error('Failed to set cache after eviction', { error: retryError, weekKey, section }, 'LocalCache');
          // Cache is disabled for this operation, but app continues
        }
      } else {
        // Other errors (security, etc.)
        logger.error('localStorage setItem failed', { error, weekKey, section }, 'LocalCache');
      }
    }
  },
  hasVisitedBefore(): boolean {
    if (!USE_CACHE) return false;
    try {
      return localStorage.getItem(VISITED_KEY) === 'true';
    } catch {
      return false;
    }
  },
  clearWeek(weekKey: string) {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)!;
      if (k.startsWith(`dash:${weekKey}:`)) keys.push(k);
    }
    keys.forEach(k => localStorage.removeItem(k));
  },
  clear(weekKey: string, section: string) {
    if (!USE_CACHE) return;
    localStorage.removeItem(ns(weekKey, section));
  },
  clearSection(section: string) {
    if (!USE_CACHE) return;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)!;
      if (k.startsWith(`dash:`) && k.endsWith(`:${section}`)) {
        keys.push(k);
      }
    }
    keys.forEach(k => localStorage.removeItem(k));
  }
}
